import { useTranslation } from "react-i18next";

import { enumsectionToI18nKey } from "../../../generated/enums";
import { Tabs, type Tab } from "../../common/Tabs";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { LicenseHistoryTable } from "../licenseHistory/LicenseHistoryTable";
import { LoginHistoryTable } from "../loginHistory/LoginHistoryTable";
import { RemoteConnectionsTable } from "../remoteConnection/RemoteConnectionsTable";

/** Onglet affiché. Décidé par la ROUTE, pas par un état local (voir ci-dessous). */
export type HistoryTabId = "remote" | "login" | "license";

export const HISTORY_REMOTE_PATH = "/admin/remote-connections";
export const HISTORY_LOGIN_PATH = "/admin/remote-connections/logins";
export const HISTORY_LICENSE_PATH = "/admin/remote-connections/licenses";

/**
 * Écran « Historique » (admin/PSC), deux onglets :
 *  - Connexions à distance : qui est allé sur quel produit
 *  - Connexions utilisateurs : qui s'est connecté au site, d'où, avec quoi
 *  - Licences : qui a attribué ou retiré quelles licences, et à quel stock
 *
 * L'onglet actif vient de l'URL et NON d'un useState : un admin doit pouvoir envoyer un
 * lien vers le bon onglet, et le bouton Retour du navigateur doit fonctionner. Les deux
 * casseraient avec un état local.
 *
 * L'ancienne URL (/admin/remote-connections) reste l'onglet par défaut : aucun lien
 * existant ne casse.
 *
 * Le `Header` est rendu ICI, une seule fois au-dessus des onglets — les deux tables ne
 * portent donc plus le leur.
 */
export const HistoryPage = ({ tab }: { tab: HistoryTabId }) => {
  const { t } = useTranslation();

  const tabs: Tab[] = [
    // `shortLabel` + `icon` : affiches SOUS `md` uniquement (cf. Tabs). Les trois
    // libelles complets ne tiennent pas sur la largeur d'un telephone.
    {
      id: "remote",
      label: t("history-tab-remote"),
      shortLabel: t("history-tab-remote-short"),
      icon: "desktop",
      to: HISTORY_REMOTE_PATH,
    },
    {
      id: "login",
      label: t("history-tab-login"),
      shortLabel: t("history-tab-login-short"),
      icon: "right-to-bracket",
      to: HISTORY_LOGIN_PATH,
    },
    {
      id: "license",
      label: t("history-tab-license"),
      shortLabel: t("history-tab-license-short"),
      icon: "key",
      to: HISTORY_LICENSE_PATH,
    },
  ];

  return (
    <div className="p-4">
      <Header
        title={t(enumsectionToI18nKey["section_history"])}
        icon={sectionToIcon.section_history}
      />

      <Tabs tabs={tabs} activeId={tab} />

      <div
        role="tabpanel"
        id={`panel-${tab}`}
        aria-labelledby={`tab-${tab}`}
      >
        {tab === "license" ? (
          <LicenseHistoryTable />
        ) : tab === "login" ? (
          <LoginHistoryTable />
        ) : (
          <RemoteConnectionsTable />
        )}
      </div>
    </div>
  );
};
